/**
 * DrillTable's pure column and cell handling — which columns a heterogeneous row set has,
 * how each scalar renders, and the CSV. No React, so it is directly testable and keeps the
 * table component inside its CD-1 file budget.
 */

import { formatCurrency } from '../../utils/formatters'
import { downloadCSV, generateCSV } from '../../utils/csvExport'
import type { DrillRow } from '../../types/metrics'
import { EM_DASH } from './cellFormat'

/** The union of row keys in first-seen order (a Group union serves heterogeneous fm/ra
 * rows by design — models/drill.py rows contract), minus anything explicitly hidden. */
export function columnsOf(rows: DrillRow[], hidden?: ReadonlySet<string>): string[] {
  const columns: string[] = []
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!columns.includes(key) && !hidden?.has(key)) columns.push(key)
    }
  }
  return columns
}

/** Currency columns render as £ 2 dp; GM% columns as a percent — one source so the
 * cost/margin columns format the same everywhere they appear. Goods and Charges are the
 * margin fact's split of LineValue (billed = goods + the invoice's apportioned carriage);
 * they are money too, and rendering them as bare numbers beside a £ column read as though
 * they were quantities.
 *
 * `Cost`/`GM%` are the MARGIN fact's, which serves one cost; `Std*`/`Actual*` are the ORDER
 * BOOK's pair. Both sets are listed because both facts render through this table.
 *
 * `Goods` + `Charges` + `Ad-hoc` == `LineValue`. Charges is the goods document's own carriage,
 * apportioned; Ad-hoc is a document that bills or credits with NO goods line, carried whole.
 * The two were one column until 24 Aug 2026, which made a £106,589.90 document read as
 * carriage — see margin_line_shape._split_charges. */
// ⚠ `Costed` IS NOT `Cost`, and a Set does not warn you (24 Aug 2026). It shipped as a raw
// float — `536541.2693258427` beside `£383,828.17` — because the column was added to the mart
// and the name was never added here. Any new money column has to be listed, by its own name.
const CURRENCY_COLUMNS = new Set(
  ['LineValue', 'Costed', 'Cost', 'StdCost', 'ActualCost', 'Goods', 'Charges', 'Ad-hoc']
)
const PERCENT_COLUMNS = new Set(['GM%', 'StdGM%', 'ActualGM%'])

/** Render one scalar: £ columns 2 dp, GM% columns as %, ISO datetimes trimmed to their
 * date, other numbers localised, null an em-dash. */
export function formatDrillValue(column: string, value: DrillRow[string]): string {
  if (value === null || value === undefined) return EM_DASH
  if (typeof value === 'number') {
    if (CURRENCY_COLUMNS.has(column)) return formatCurrency(value, '£', 2)
    if (PERCENT_COLUMNS.has(column)) return `${value.toFixed(1)}%`
    return Number.isInteger(value) ? value.toLocaleString('en-GB') : String(value)
  }
  const text = String(value)
  return /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.slice(0, 10) : text
}

function csvCell(value: DrillRow[string]): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export function exportRows(rows: DrillRow[], columns: string[], name: string): void {
  const lines = [columns, ...rows.map((row) => columns.map((c) => csvCell(row[c])))]
  downloadCSV(generateCSV(lines), `${name}.csv`)
}
